import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faBoxOpen, faLayerGroup, faCheckCircle,
  faStore, faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { Loader } from '../Loader/Loader';
import { Msg200or201, Msg500 } from '../Messages/Messages';
import { decryptData } from '../../utils/storageCrypto';
import BulkAssignProductsToHubsModal from './BulkAssignProductsToHubsModal';
import HubAssignedProductsManager from './HubAssignedProductsManager';

// ---------------------------------------------------------------------------
// Screen: HubProductAssignmentDashboard
// Purpose: Lets an admin pick one or many hubs and assign catalog products to
// them in bulk, or open a single hub to manage what's already assigned there.
//
// Backend endpoints assumed (adjust to match actual API contracts):
//   GET  hub/get-all-hubs                                (already used elsewhere)
//   GET  hub-product/get-assignment-counts                -> { hubId: assignedCount }
//   POST hub-product/bulk-assign                           (used by the modal)
// ---------------------------------------------------------------------------

const HubProductAssignmentDashboard = () => {
  const [hubs, setHubs] = useState([]);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHubIds, setSelectedHubIds] = useState({});
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [manageHub, setManageHub] = useState(null);
  const [popup, setPopup] = useState({ type: '', message: '' });

  const fetchUrl = process.env.REACT_APP_FORE_BRICKBUCKET;

  const getAuthHeaders = () => {
    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    return { 'Content-Type': 'application/json', 'x-token': token };
  };

  useEffect(() => {
    fetchHubs();
  }, []);

  const fetchHubs = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${fetchUrl}hub/get-all-hubs`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        setHubs(data.data || []);
        fetchAssignmentCounts(data.data || []);
      } else {
        console.error(data.message);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch hubs:', error);
      setIsLoading(false);
    }
  };

  const fetchAssignmentCounts = async (hubList) => {
    try {
      const response = await fetch(`${fetchUrl}hub-product/get-assignment-counts`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      if (response.ok) {
        // Expected shape: { data: { [hubId]: count } }
        setAssignmentCounts(data.data || {});
      }
    } catch (error) {
      console.error('Failed to fetch assignment counts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredHubs = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return hubs;
    return hubs.filter(hub =>
      hub.hub_name?.toLowerCase().includes(term) ||
      hub.hub_number?.toString().toLowerCase().includes(term) ||
      hub.city?.toLowerCase().includes(term) ||
      hub.zoneId?.zone_name?.toLowerCase().includes(term)
    );
  }, [hubs, searchTerm]);

  const selectedCount = Object.values(selectedHubIds).filter(Boolean).length;
  const allFilteredSelected = filteredHubs.length > 0 && filteredHubs.every(h => selectedHubIds[h._id]);

  const toggleHub = (hubId) => {
    setSelectedHubIds(prev => ({ ...prev, [hubId]: !prev[hubId] }));
  };

  const toggleSelectAllFiltered = () => {
    const next = { ...selectedHubIds };
    filteredHubs.forEach(h => { next[h._id] = !allFilteredSelected; });
    setSelectedHubIds(next);
  };

  const selectedHubs = hubs.filter(h => selectedHubIds[h._id]);

  const handleBulkAssignComplete = (result) => {
    setShowBulkAssign(false);
    setSelectedHubIds({});
    setPopup({ type: '200', message: `Products assigned to ${result.hubCount} hub(s) successfully.` });
    fetchHubs();
    setTimeout(() => setPopup({ type: '', message: '' }), 2500);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="bg-white flex flex-row w-full justify-left h-full overflow-y-hidden">
      {popup.type === '200' && <Msg200or201 message={popup.message} />}
      {popup.type === '500' && <Msg500 message={popup.message} closePopup={() => setPopup({ type: '', message: '' })} />}

      <div className="flex flex-row w-full">
        <div className="w-full flex-1">
          <div className="flex flex-col w-full h-full justify-center items-center">
            <div className="w-full h-full flex flex-col justify-between">

              {/* Header */}
              <div className="w-full border-b flex items-center">
                <div className="flex w-full justify-between px-4 mt-4 mb-4 items-center flex-wrap gap-3">
                  <div className="flex flex-row items-center gap-3">
                    <FontAwesomeIcon icon={faStore} className="text-[#f7525a] text-lg" />
                    <h1 className="text-lg font-bold whitespace-nowrap">Assign Products to Hubs</h1>
                  </div>

                  <div className="relative w-full sm:w-72">
                    <FontAwesomeIcon
                      icon={faSearch}
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Search hubs by name, number, city, zone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                  </div>

                  <button
                    onClick={() => setShowBulkAssign(true)}
                    disabled={selectedCount === 0}
                    className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                      selectedCount > 0
                        ? 'bg-[#F7525A] text-white hover:bg-[#e04850]'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FontAwesomeIcon icon={faBoxOpen} className="mr-2" />
                    Assign Products {selectedCount > 0 ? `(${selectedCount} hub${selectedCount !== 1 ? 's' : ''})` : ''}
                  </button>
                </div>
              </div>

              {/* Selection helper bar */}
              <div className="px-4 py-2 bg-[#fafafc] border-b flex items-center justify-between text-sm text-[#6c7184]">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAllFiltered}
                    className="rounded"
                  />
                  Select all {filteredHubs.length ? `(${filteredHubs.length})` : ''} visible hubs
                </label>
                <span>{selectedCount} hub{selectedCount !== 1 ? 's' : ''} selected</span>
              </div>

              {/* Hub grid */}
              <div className="w-full h-full bg-white overflow-y-auto">
                {filteredHubs.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                    <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hubs found</h3>
                    <p className="text-sm text-gray-600">Try adjusting your search criteria.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                    {filteredHubs.map(hub => {
                      const isSelected = !!selectedHubIds[hub._id];
                      const assignedCount = assignmentCounts[hub._id] || 0;
                      return (
                        <div
                          key={hub._id}
                          className={`border rounded-lg p-4 flex flex-col gap-3 transition-colors ${
                            isSelected ? 'border-[#F7525A] bg-red-50' : 'border-gray-200 bg-white'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <label className="flex items-start gap-3 cursor-pointer flex-1 min-w-0">
                              <input
                                type="checkbox"
                                className="mt-1 rounded"
                                checked={isSelected}
                                onChange={() => toggleHub(hub._id)}
                              />
                              <div className="min-w-0">
                                <h3 className="font-semibold text-gray-900 truncate">{hub.hub_name}</h3>
                                <p className="text-xs text-gray-500">Hub #{hub.hub_number}</p>
                              </div>
                            </label>
                            {isSelected && (
                              <FontAwesomeIcon icon={faCheckCircle} className="text-[#F7525A]" />
                            )}
                          </div>

                          <div className="text-xs text-gray-600 space-y-1">
                            <div>{hub.city || hub.address1 || '—'}</div>
                            <div className="flex items-center gap-1">
                              <FontAwesomeIcon icon={faLayerGroup} className="text-gray-400" />
                              Zone: {hub.zoneId?.zone_name || 'N/A'}
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-1 pt-3 border-t">
                            <span className="text-xs sm:text-sm">
                              <span className="font-semibold text-gray-900">{assignedCount}</span>
                              <span className="text-gray-500"> products assigned</span>
                            </span>
                            <button
                              onClick={() => setManageHub(hub)}
                              className="text-[#408dfb] text-xs sm:text-sm font-medium flex items-center gap-1 hover:underline"
                            >
                              Manage <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showBulkAssign && (
        <BulkAssignProductsToHubsModal
          hubs={selectedHubs}
          onClose={() => setShowBulkAssign(false)}
          onAssignmentComplete={handleBulkAssignComplete}
        />
      )}

      {manageHub && (
        <HubAssignedProductsManager
          hub={manageHub}
          onClose={() => {
            setManageHub(null);
            fetchHubs();
          }}
        />
      )}
    </div>
  );
};

export default HubProductAssignmentDashboard;
